import React from 'react';
import { Helmet } from "react-helmet-async";
import {
  Switch,
} from "react-router-dom";
import { renderRoutes } from 'react-router-config';
import styled, { createGlobalStyle } from 'styled-components'
import { CookiesProvider } from 'react-cookie'
import { ModalProvider } from 'styled-react-modal'
import { IntlProvider, FormattedMessage, useIntl } from "react-intl"
import { getAssistantDisplayName } from './utils/assistantName';
import { DFPSlotsProvider } from 'react-dfp';
import { MemoCacheProvider } from './hooks/AppMemoCache';
import { ToastContainer, Bounce } from 'react-toastify';

import {
  Wrapper,
  Container,
  Link as InternalLink
} from './StyledComponents'
import GaTracker from './components/GaTracker';
import messages from "./common/lang/messages";
import { LoggedInContextProvider } from './auth/AuthContext'
import { TenancyContext } from './client/useTenant'
import { LangContext } from './useLang'
import TENANT_TO_LANG from './common/TENANT_TO_LANG'
import { Header } from './Header';
import Routes from './routes';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import { ThemeManager, useThemePreference } from './theme';
import { loadHistorywormFonts } from './loadHistorywormFonts';
import useTenant from './client/useTenant';
import { historywormFooterHubLinks } from './common/const/historywormOutboundLinks';

const Background = styled.div`
  overflow: scroll;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  min-width: 100vw;
  min-height: 100vh;
  width: fit-content;
  height: 100vh;
  z-index: 10002;
  background-color: ${({ theme }) => theme.colors.overlay};
`;

const Footer = styled.footer`
  width: 100%;
  padding: 48px 0 24px;
  background: linear-gradient(180deg, ${({ theme }) => theme.colors.footerBgStart} 0%, ${({ theme }) => theme.colors.footerBgEnd} 100%);
  border-top: 1px solid ${({ theme }) => theme.colors.footerBorder};
`;

const FooterInner = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 576px) {
    padding: 0 16px;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const FooterCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterHeading = styled.h4`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.footerHeading};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 16px;
`;

const FooterLink = styled.a`
  color: ${({ theme }) => theme.colors.footerText};
  font-size: 0.9rem;
  text-decoration: none;
  padding: 4px 0;
  display: block;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.footerHeading};
  }
`;

const FooterNavLink = styled(InternalLink)`
  color: ${({ theme }) => theme.colors.footerText};
  font-size: 0.9rem;
  padding: 4px 0;
  display: block;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.footerHeading};
  }
`;

const FooterBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const FooterBrandIcon = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const FooterBrandText = styled.span`
  font-weight: 700;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.footerHeading};
`;

const FooterDesc = styled.p`
  color: ${({ theme }) => theme.colors.footerText};
  font-size: 0.85rem;
  line-height: 1.6;
  margin: 0;
`;

const FooterDivider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.footerBorder};
  margin: 32px 0 16px;
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const FooterCopyright = styled.span`
  color: ${({ theme }) => theme.colors.footerCopyright};
  font-size: 0.8rem;
  white-space: nowrap;
`;

const GlobalStyle = createGlobalStyle`
  :root {
    --skeleton-bg: ${({ theme }) => theme.colors.surfaceHover};
  }

  html[data-tenant="historyworm"] body {
    background-color: ${({ theme }) =>
      theme.mode === 'light' ? '#faf8f4' : theme.colors.background};
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  body {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-family: ${({ theme }) => theme.typography.fontFamily};
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.typography.fontFamilyDisplay};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }
`;

const Body = styled(Container)`
  margin: 0;
  @media (min-width: 576px) {
    max-width: none;
  }
`;

export type Tenant = keyof typeof TENANT_TO_LANG;
export type Lang = (typeof TENANT_TO_LANG)[Tenant];

export interface InitialTheme {
  preference: 'system';
  systemMode: 'dark' | 'light';
}

export interface AppProps {
  tenant: Tenant;
  lang: Lang;
  initialTheme?: InitialTheme;
}

function FooterComponent(): React.ReactElement {
  const intl = useIntl();
  const tenant = useTenant();
  const assistantName = getAssistantDisplayName(intl.locale);

  return (
    <Footer role="contentinfo">
      <FooterInner>
        <FooterGrid>
          <FooterCol>
            <FooterBrand>
              <FooterBrandIcon src="/static/favicon.png" alt="" />
              <FooterBrandText>
                <FormattedMessage id="name" />
              </FooterBrandText>
            </FooterBrand>
            <FooterDesc>
              <FormattedMessage id="footer.about.desc"
                defaultMessage="Osmanlı'dan günümüze gazete sayfalarının dijital arşivi." />
            </FooterDesc>
          </FooterCol>

          <FooterCol>
            <FooterHeading>
              <FormattedMessage id="footer.quickLinks" defaultMessage="Hızlı Bağlantılar" />
            </FooterHeading>
            <FooterNavLink to="/ara">
              <FormattedMessage id="footer.search" defaultMessage="Arama" />
            </FooterNavLink>
            <FooterNavLink to="/arastirmalar">
              <FormattedMessage id="footer.research" defaultMessage="Araştırmalar" />
            </FooterNavLink>
            <FooterNavLink to="/ai-tarihci">
              <FormattedMessage id="footer.assistant_chat" values={{ name: assistantName }} />
            </FooterNavLink>
            <FooterNavLink to="/collections">
              <FormattedMessage id="footer.collections" defaultMessage="Koleksiyonlar" />
            </FooterNavLink>
            <FooterNavLink to="/gunun-mansetleri">
              <FormattedMessage id="footer.todaysHeadlines" defaultMessage="Günün manşetleri" />
            </FooterNavLink>
            <FooterNavLink to="/privacy">
              <FormattedMessage id="footer.privacy" defaultMessage="Gizlilik Politikası" />
            </FooterNavLink>
          </FooterCol>

          <FooterCol>
            <FooterHeading>
              <FormattedMessage id="footer.sisterSites" defaultMessage="Kardeş Siteler" />
            </FooterHeading>
            {tenant === 'gastearsivi' ? (
              historywormFooterHubLinks.map(link => (
                <FooterLink
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage id={link.messageId} defaultMessage={link.defaultMessage} />
                </FooterLink>
              ))
            ) : (
              <FooterLink href="https://www.historyworm.com" target="_blank" rel="noopener noreferrer">History Worm</FooterLink>
            )}
            <FooterLink href="https://www.fiyatarsivi.com" target="_blank" rel="noopener noreferrer">Fiyat Arşivi</FooterLink>
            <FooterLink href="https://www.nedemek.page" target="_blank" rel="noopener noreferrer">Ne Demek</FooterLink>
            <FooterLink href="https://www.wikiwhat.page" target="_blank" rel="noopener noreferrer">Wiki What</FooterLink>
            <FooterLink href="https://www.pricearchive.page" target="_blank" rel="noopener noreferrer">Price Archive</FooterLink>
          </FooterCol>

          <FooterCol>
            <FooterHeading>
              <FormattedMessage id="footer.contactTitle" defaultMessage="İletişim" />
            </FooterHeading>
            <FooterLink href={`mailto:${intl.formatMessage({ id: "footer.email" })}`}>
              <FormattedMessage id="footer.email" />
            </FooterLink>
          </FooterCol>
        </FooterGrid>

        <FooterDivider />

        <FooterBottom>
          <FooterDesc style={{ maxWidth: 640 }}>
            <FormattedMessage id="footer.emailCta" />
          </FooterDesc>
          <FooterCopyright suppressHydrationWarning>
            © {new Date().getFullYear()} <FormattedMessage id="name" />
          </FooterCopyright>
        </FooterBottom>
      </FooterInner>
    </Footer>
  );
}

function ToastContainerThemed(): React.ReactElement {
  const { resolvedMode } = useThemePreference();
  return (
    <ToastContainer
      position="top-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick={false}
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={resolvedMode === 'dark' ? 'dark' : 'light'}
      transition={Bounce}
    />
  );
}

function App(props: AppProps): React.ReactElement {
  const { tenant, lang, initialTheme } = props;
  const [showForgot, setShowForgot] = React.useState(false);
  loadHistorywormFonts(tenant);

  return (
    <TenancyContext.Provider value={tenant}>
      <LangContext.Provider value={{ lang: TENANT_TO_LANG[tenant] }}>
        <CookiesProvider>
          <IntlProvider locale={lang} defaultLocale="tr" messages={messages[lang]}>
            <LoggedInContextProvider>
              <ThemeManager initialTheme={initialTheme}>
                <MemoCacheProvider>
                  <GaTracker>
                    <DFPSlotsProvider dfpNetworkId="22024215299">
                      <GlobalStyle />
                      <Helmet
                        defer={false}
                        htmlAttributes={{ lang, 'data-tenant': tenant }}
                      >
                        <link rel="icon" href={`/static/favicon.png`} />
                        {/* manifest.json provides metadata used when your web app is installed on a
                            user's mobile device or desktop. See https://developers.google.com/web/fundamentals/web-app-manifest/ */}
                        <link rel="manifest" href={`/static/manifest.json`} />
                        <meta property="fb:app_id" content="1665553766959662" />
                        <link title="Gaste Arşivi" rel="search" type="application/opensearchdescription+xml" href="/odesc.xml" />
                      </Helmet>
                      <Wrapper>
                        <ToastContainerThemed />
                        <ModalProvider backgroundComponent={Background}>
                          <Header />
                          <ForgotPasswordModal isOpen={showForgot} onClose={() => setShowForgot(false)} />
                          <Body>
                            <Switch>
                              {renderRoutes(Routes)}
                            </Switch>
                          </Body>
                          <FooterComponent />
                        </ModalProvider>
                      </Wrapper>
                    </DFPSlotsProvider>
                  </GaTracker>
                </MemoCacheProvider>
              </ThemeManager>
            </LoggedInContextProvider>
          </IntlProvider>
        </CookiesProvider>
      </LangContext.Provider>
    </TenancyContext.Provider>
  );
}

export default App;
